import { useMemo, memo } from "react";
import { connect } from "react-redux";
import { activeTeamTemplateSizesSelector } from "state/entities/teamTemplateSizes/teamTemplateSizesSelectors";
import { TeamTemplateSize } from "state/entities/teamTemplateSizes/teamTemplateSizesActions";
import {
  getTeamSizeNameForDesign,
  isCustomDesign
} from "views/components/Editor/sidebar/tabs/resize/tabs/designs/teamSizeMatch";
import { formatDesignMeasurements } from "lib/templateSize";

interface DesignLike {
  templateCode?: string;
  templateSize?: string;
  measurementUnits?: string;
  width?: number;
  height?: number;
  widthInMm?: number;
  heightInMm?: number;
  widthInInches?: number;
  heightInInches?: number;
}

interface OwnProps {
  design: DesignLike;
  className?: string;
  title?: boolean;
  as?: "div" | "span";
  // When set, append the design's measurements in parentheses after a named
  // label (team size name or template size). Suppressed when the label already
  // is the measurements string, so an unmatched custom design renders the
  // measurements once rather than "1080 x 1080px (1080 x 1080px)".
  withMeasurements?: boolean;
}

interface StateProps {
  teamSizes: TeamTemplateSize[];
}

type Props = OwnProps & StateProps;

const DesignSizeLabel = ({
  design,
  className,
  title,
  as,
  withMeasurements,
  teamSizes
}: Props) => {
  const text = useMemo(() => {
    const teamSizeName = getTeamSizeNameForDesign(design, teamSizes);
    const measurements = formatDesignMeasurements(design);

    // Primary label: a matched team size name wins; otherwise a custom design
    // shows its measurements (falling back to templateSize / "Custom" when the
    // measurements are unusable); standard designs keep their template size.
    let label: string;
    let labelIsMeasurements = false;
    if (teamSizeName) {
      label = teamSizeName;
    } else if (isCustomDesign(design) && measurements) {
      label = measurements;
      labelIsMeasurements = true;
    } else {
      label = design.templateSize ?? "";
    }

    // Only append "(measurements)" behind a named label, and only when we have
    // usable measurements that aren't already the label itself.
    if (withMeasurements && measurements && !labelIsMeasurements) {
      return `${label} (${measurements})`;
    }
    return label;
  }, [design, teamSizes, withMeasurements]);

  // Default to <div> so text-overflow ellipsis driven by a flex parent's
  // `overflow: hidden` className works reliably. The popout header uses the
  // label inline next to other text and opts in to <span>.
  const Tag = as ?? "div";

  return (
    <Tag className={className} title={title ? text : undefined}>
      {text}
    </Tag>
  );
};

const mapStateToProps = (state: any): StateProps => ({
  teamSizes: activeTeamTemplateSizesSelector(state)
});

export default connect(mapStateToProps)(memo(DesignSizeLabel));
